import { useTranslation } from 'react-i18next';
import { Input } from '~/components/ui';
import { BundleQuantityInput } from '~/components/ui/BundleQuantityInput';
import { FieldError } from '~/components/ui/inputs/FieldError';
import { getCurrencySymbol, priceUnitLabel } from '~/utils';
import { priceMessageParams, MIN_POSITIVE_PRICE } from '~/utils/moneyBounds';
import type { Currency, UnitCode } from '~/graphql/graphql';
import type { RequestQuoteLength } from './RequestQuoteModal';
import {
  bundleSizeIsMissing,
  priceIsInvalid,
  quantityIsInvalid,
  EMPTY_LENGTH_INPUT,
  PRICE_FIELDS,
  type LengthInput,
} from './requestQuote';

type SelectLengthsStepProps = {
  lengths: RequestQuoteLength[];
  priceUnit: UnitCode;
  currency: Currency;
  selectedLengthIds: Set<string>;
  onToggleLength: (id: string) => void;
  inputsByLengthId: Map<string, LengthInput>;
  onUpdateInput: (id: string, field: keyof LengthInput, value: string) => void;
  touchedLengthIds: Set<string>;
  onTouchLength: (id: string) => void;
  nextAttempted: boolean;
};

export function SelectLengthsStep({
  lengths,
  priceUnit,
  currency,
  selectedLengthIds,
  onToggleLength,
  inputsByLengthId,
  onUpdateInput,
  touchedLengthIds,
  onTouchLength,
  nextAttempted,
}: Readonly<SelectLengthsStepProps>) {
  const { t } = useTranslation('order-builder');
  const currencySymbol = getCurrencySymbol(currency);
  const inputFor = (id: string) =>
    inputsByLengthId.get(id) ?? EMPTY_LENGTH_INPUT;

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-neut-sec">
          <th className="w-8 py-2">
            <span className="sr-only">
              {t('order_builder.request_quote.include_column')}
            </span>
          </th>
          <th className="py-2 pr-3">
            {t('order_builder.request_quote.length_column')}
          </th>
          <th className="py-2">
            {t('order_builder.request_quote.quantity_column')}
          </th>
          <th className="py-2">
            {t('order_builder.request_quote.pickup_price_column')}
          </th>
          <th className="py-2">
            {t('order_builder.request_quote.delivery_price_column')}
          </th>
        </tr>
      </thead>
      <tbody>
        {lengths.map((length) => {
          const id = length.platformProductLengthId;
          const input = inputFor(id);
          const selected = selectedLengthIds.has(id);
          const quantityLabel = t('order_builder.request_quote.quantity_for', {
            length: length.label,
          });
          const revealQuantityError = touchedLengthIds.has(id) || nextAttempted;
          const quantityError =
            quantityIsInvalid(selected, Number(input.quantity)) &&
            revealQuantityError
              ? t('order_builder.request_quote.invalid_quantity')
              : undefined;
          const quantityErrorId = `request-quote-quantity-error-${id}`;
          const missingBundleSize = bundleSizeIsMissing(
            selected,
            length.platformProductsLength,
            length.defaultQuantity.unit
          )
            ? t('order_builder.request_quote.missing_bundle_size')
            : undefined;
          const missingBundleSizeId = `request-quote-missing-bundle-size-${id}`;
          const priceErrorFor = (value: string) =>
            priceIsInvalid(selected, value)
              ? t('order_builder.request_quote.invalid_price', {
                  max: priceMessageParams.max,
                })
              : undefined;
          const priceIcons = {
            leftIcon: (
              <span className="pl-3 text-xs text-neut-500">
                {currencySymbol}
              </span>
            ),
            rightIcon: (
              <span className="pr-3 text-xs text-neut-500">
                /{priceUnitLabel(priceUnit)}
              </span>
            ),
          };
          return (
            <tr
              key={id}
              className="border-t border-neut-border align-top"
              data-testid={`request-quote-length-${id}`}
            >
              <td className="py-2">
                <div className="flex h-10 items-center">
                  <input
                    type="checkbox"
                    id={`request-quote-include-${id}`}
                    aria-label={length.label}
                    checked={selected}
                    onChange={() => onToggleLength(id)}
                  />
                </div>
              </td>
              <td className="py-2 pr-3 font-medium text-neut-prim">
                <div className="flex h-10 items-center">{length.label}</div>
              </td>
              <td className="py-2 pr-3">
                <div
                  role="group"
                  aria-label={quantityLabel}
                  aria-describedby={
                    [
                      quantityError ? quantityErrorId : undefined,
                      missingBundleSize ? missingBundleSizeId : undefined,
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                  className="flex flex-col gap-2"
                >
                  <BundleQuantityInput
                    quantity={{
                      value: Number(input.quantity) || 0,
                      unit: length.defaultQuantity.unit,
                    }}
                    platformProductsLength={length.platformProductsLength}
                    onBundleChange={(next) => {
                      onTouchLength(id);
                      onUpdateInput(id, 'quantity', String(next.value));
                    }}
                  />
                  {quantityError && (
                    <FieldError id={quantityErrorId} tone="warning">
                      {quantityError}
                    </FieldError>
                  )}
                  {missingBundleSize && (
                    <FieldError id={missingBundleSizeId} tone="warning">
                      {missingBundleSize}
                    </FieldError>
                  )}
                </div>
              </td>
              {PRICE_FIELDS.map(({ field, labelKey }) => (
                <td key={field} className="py-2 pr-3">
                  <Input
                    name={`${field}-${id}`}
                    type="number"
                    min={MIN_POSITIVE_PRICE}
                    step="0.01"
                    aria-label={t(labelKey, { length: length.label })}
                    value={input[field]}
                    error={priceErrorFor(input[field])}
                    onChange={(_, value) => onUpdateInput(id, field, value)}
                    {...priceIcons}
                  />
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
